import {
  Badge,
  Button,
  Group,
  Modal,
  Paper,
  SimpleGrid,
  Stack,
  Table,
  Text,
  TextInput,
  Timeline,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { IconArrowLeft, IconCheck } from "@tabler/icons-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { DataTableShell, PageHeader } from "../../components/ui";
import { adminApi } from "../../lib/api";
import { NAV_ICONS } from "../../lib/navCopy";
import { ApiError } from "../../lib/apiClient";
import { formatDate, formatPaise, INVOICE_STATUS_LABELS, invoiceStatusColor } from "../../lib/format";
import type { InvoiceLine } from "../../types/api";
import { axioTokens } from "../../theme";

export function AdminInvoiceDetailPage() {
  const { invoiceId = "" } = useParams();
  const queryClient = useQueryClient();
  const [lineToPay, setLineToPay] = useState<InvoiceLine | null>(null);
  const [fullPayOpen, setFullPayOpen] = useState(false);
  const [paymentReference, setPaymentReference] = useState("");

  const invoiceQuery = useQuery({
    queryKey: ["admin-invoice", invoiceId],
    queryFn: () => adminApi.getInvoice(invoiceId),
    enabled: Boolean(invoiceId),
  });

  const invoice = invoiceQuery.data;
  const unpaid = invoice?.lines.filter((line) => !line.is_paid) ?? [];

  function invalidate() {
    void queryClient.invalidateQueries({ queryKey: ["admin-invoice", invoiceId] });
    void queryClient.invalidateQueries({ queryKey: ["admin-invoices"] });
  }

  const issueMutation = useMutation({
    mutationFn: () => adminApi.issueInvoice(invoiceId),
    onSuccess: () => {
      notifications.show({ color: "green", message: "Invoice issued" });
      invalidate();
    },
    onError: (error) => {
      notifications.show({
        color: "red",
        title: "Issue failed",
        message: error instanceof ApiError ? error.message : "Request failed",
      });
    },
  });

  const linePayMutation = useMutation({
    mutationFn: () =>
      adminApi.approveInvoiceLine(invoiceId, lineToPay!.id, { payment_reference: paymentReference.trim() }),
    onSuccess: () => {
      notifications.show({ color: "green", message: "Line marked paid" });
      setLineToPay(null);
      setPaymentReference("");
      invalidate();
    },
    onError: (error) => {
      notifications.show({
        color: "red",
        title: "Could not record payment",
        message: error instanceof ApiError ? error.message : "Request failed",
      });
    },
  });

  const fullPayMutation = useMutation({
    mutationFn: () => adminApi.approveInvoicePayment(invoiceId, { payment_reference: paymentReference.trim() }),
    onSuccess: () => {
      notifications.show({ color: "green", message: "Invoice marked fully paid" });
      setFullPayOpen(false);
      setPaymentReference("");
      invalidate();
    },
    onError: (error) => {
      notifications.show({
        color: "red",
        title: "Could not record payment",
        message: error instanceof ApiError ? error.message : "Request failed",
      });
    },
  });

  return (
    <Stack gap="lg">
      <PageHeader
        icon={NAV_ICONS.invoices}
        title={invoice?.invoice_number ?? "Invoice"}
        subtitle={invoice ? `${invoice.company_name} · ${invoice.company_legal_name}` : "Loading invoice"}
        breadcrumb={
          <Button
            component={Link}
            to="/admin/finance/invoices"
            variant="subtle"
            size="compact-sm"
            leftSection={<IconArrowLeft size="var(--axio-unit-14)" />}
          >
            All invoices
          </Button>
        }
        actions={
          invoice ? (
            <Group>
              {invoice.status === "draft" && (
                <Button variant="light" loading={issueMutation.isPending} onClick={() => issueMutation.mutate()}>
                  Issue invoice
                </Button>
              )}
              {invoice.status !== "draft" && unpaid.length > 0 && (
                <Button
                  onClick={() => {
                    setPaymentReference("");
                    setFullPayOpen(true);
                  }}
                >
                  Approve full payment
                </Button>
              )}
            </Group>
          ) : undefined
        }
      />

      {invoice && (
        <SimpleGrid cols={{ base: 1, md: 3 }} spacing="md">
          <Paper withBorder p="md">
            <Text size="xs" c="dimmed" tt="uppercase">
              Status
            </Text>
            <Badge mt="var(--axio-unit-6)" color={invoiceStatusColor(invoice.status)} variant="light">
              {INVOICE_STATUS_LABELS[invoice.status] ?? invoice.status}
            </Badge>
            <Text size="sm" mt="sm">
              Due {formatDate(invoice.due_date)}
            </Text>
            <Text size="sm" c="dimmed">
              Period {formatDate(invoice.period_start)} – {formatDate(invoice.period_end)}
            </Text>
          </Paper>
          <Paper withBorder p="md">
            <Text size="xs" c="dimmed" tt="uppercase">
              GST
            </Text>
            <Text size="sm" mt="var(--axio-unit-6)">
              Client GSTIN {invoice.client_gstin || "—"}
            </Text>
            <Text size="sm">Our GSTIN {invoice.our_gstin || "—"}</Text>
            <Text size="sm" c="dimmed">
              HSN {invoice.hsn_sac_code} · {invoice.gst_rate_pct}%
            </Text>
          </Paper>
          <Paper withBorder p="md">
            <Text size="xs" c="dimmed" tt="uppercase">
              Totals
            </Text>
            <Text size="sm" mt="var(--axio-unit-6)">
              Subtotal {formatPaise(invoice.subtotal_paise)}
            </Text>
            <Text size="sm">
              CGST {formatPaise(invoice.cgst_paise)} · SGST {formatPaise(invoice.sgst_paise)}
            </Text>
            <Text size="sm">IGST {formatPaise(invoice.igst_paise)}</Text>
            <Text fw="var(--axio-font-weight-semibold)" mt="var(--axio-unit-4)">
              Total {formatPaise(invoice.total_paise)}
            </Text>
            <Text size="sm" c="teal">
              Received {formatPaise(invoice.amount_received_paise)}
            </Text>
            <Text size="sm" c={invoice.balance_due_paise > 0 ? "orange" : "dimmed"}>
              Balance {formatPaise(invoice.balance_due_paise)}
            </Text>
          </Paper>
        </SimpleGrid>
      )}

      <Group align="flex-start" gap="lg" wrap="wrap">
        <Stack gap="sm" style={{ flex: "var(--axio-number-one)", minWidth: "var(--axio-unit-0)" }}>
          <Text fw="var(--axio-font-weight-semibold)">Line items</Text>
          <DataTableShell
            loading={invoiceQuery.isLoading}
            error={
              invoiceQuery.isError
                ? invoiceQuery.error instanceof Error
                  ? invoiceQuery.error.message
                  : "Could not load invoice"
                : undefined
            }
            onRetry={() => void invoiceQuery.refetch()}
            isEmpty={(invoice?.lines.length ?? 0) === 0}
            empty={{ icon: NAV_ICONS.invoices, title: "No lines", description: "This invoice has no placements." }}
          >
            <Table verticalSpacing="md" highlightOnHover>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Candidate</Table.Th>
                  <Table.Th>Role</Table.Th>
                  <Table.Th>Joining</Table.Th>
                  <Table.Th>Fee</Table.Th>
                  <Table.Th>GST</Table.Th>
                  <Table.Th>Status</Table.Th>
                  <Table.Th />
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {(invoice?.lines ?? []).map((line) => (
                  <Table.Tr key={line.id}>
                    <Table.Td>{line.candidate_name ?? line.description}</Table.Td>
                    <Table.Td>{line.job_title ?? "—"}</Table.Td>
                    <Table.Td>{formatDate(line.joining_date)}</Table.Td>
                    <Table.Td>{formatPaise(line.amount_paise)}</Table.Td>
                    <Table.Td>{formatPaise(line.gst_paise)}</Table.Td>
                    <Table.Td>
                      {line.is_paid ? (
                        <Group gap="var(--axio-unit-6)">
                          <IconCheck size="var(--axio-unit-14)" color="var(--mantine-color-teal-6)" />
                          <Text size="sm">{line.payment_reference}</Text>
                        </Group>
                      ) : (
                        <Badge variant="light" color="gray">
                          Unpaid
                        </Badge>
                      )}
                    </Table.Td>
                    <Table.Td>
                      {!line.is_paid && invoice?.status !== "draft" && (
                        <Button
                          size="xs"
                          variant="light"
                          onClick={() => {
                            setPaymentReference("");
                            setLineToPay(line);
                          }}
                        >
                          Mark paid
                        </Button>
                      )}
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </DataTableShell>
        </Stack>

        <Paper withBorder p="md" style={{ width: "var(--axio-unit-320)", flexShrink: "var(--axio-number-zero)" }}>
          <Text fw="var(--axio-font-weight-semibold)" mb="sm">
            Payment timeline
          </Text>
          {(invoice?.payment_events.length ?? 0) === 0 ? (
            <Text size="sm" c="dimmed">
              No payments recorded yet.
            </Text>
          ) : (
            <Timeline bulletSize={axioTokens.unit[16]} lineWidth={axioTokens.strokeWidth.strong}>
              {invoice!.payment_events.map((event) => (
                <Timeline.Item key={event.line_id} title={formatPaise(event.amount_paise + event.gst_paise)}>
                  <Text size="sm">{event.description}</Text>
                  <Text size="xs" c="dimmed">
                    {event.payment_reference} · {formatDate(event.paid_at)}
                  </Text>
                </Timeline.Item>
              ))}
            </Timeline>
          )}
        </Paper>
      </Group>

      <Modal opened={lineToPay != null} onClose={() => setLineToPay(null)} title="Record line payment" centered>
        <Stack>
          <Text size="sm">
            {lineToPay?.candidate_name} · {formatPaise((lineToPay?.amount_paise ?? 0) + (lineToPay?.gst_paise ?? 0))}
          </Text>
          <TextInput
            label="Bank / NEFT reference"
            value={paymentReference}
            onChange={(event) => setPaymentReference(event.currentTarget.value)}
          />
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setLineToPay(null)}>
              Cancel
            </Button>
            <Button
              loading={linePayMutation.isPending}
              disabled={!paymentReference.trim()}
              onClick={() => linePayMutation.mutate()}
            >
              Mark paid
            </Button>
          </Group>
        </Stack>
      </Modal>

      <Modal opened={fullPayOpen} onClose={() => setFullPayOpen(false)} title="Approve full payment" centered>
        <Stack>
          <Text size="sm">
            This marks all unpaid lines as paid. Recruiter payouts are not released automatically.
          </Text>
          <TextInput
            label="Bank / NEFT reference"
            value={paymentReference}
            onChange={(event) => setPaymentReference(event.currentTarget.value)}
          />
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setFullPayOpen(false)}>
              Cancel
            </Button>
            <Button
              loading={fullPayMutation.isPending}
              disabled={!paymentReference.trim()}
              onClick={() => fullPayMutation.mutate()}
            >
              Approve payment
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  );
}
